import { Dispatch, SetStateAction } from 'react';
import styled from 'styled-components';

import { BASE, CAMPAIGN, TemplateSource, TemplateType, WORKFLOW } from 'constants/templateTypes';
import { getIsTriggeredCampaignType, getIsWhatsAppCampaign } from 'modules/campaigns/lib/utils';
import { AllMessageMedium, CampaignType } from 'modules/domains/campaign';
import { SelectedMessageMedium, TemplateMetadata } from 'modules/templateEditor/common/types';
import { GhostButton } from 'ui/button';
import { FilterMenuTriggerButton } from 'ui/indexPages/filters/FilterMenuTriggerButton';
import { InputSearch } from 'ui/inputs';
import { ALIGNMENT, Menu } from 'ui/menu';
import { Tab, TabGroup } from 'ui/tabs';
import { colors, heading5, spacing } from 'ui/theme';
import { pxToRem } from 'utils/convert';
import { useWhatsAppMessageChannelFeature } from 'utils/hooks/featureFlags';

import {
  ATTACH_TEMPLATE_TAKEOVER_HEADER_ACTIONS_ID,
  ATTACH_TEMPLATE_TAKEOVER_SEARCH_INPUT_ID,
  displayContent,
  TEMPLATE_TYPE_MENU_OPTIONS,
} from './AttachTemplateTakeover.constant';
import { WhatsAppTemplatePickerBanner } from './WhatsAppTemplatePickerBanner';

const ActionsContainer = styled.section`
  align-items: center;
  display: flex;
  justify-content: space-between;

  > :nth-child(2) {
    min-width: ${pxToRem(200)};
  }
`;

const ContainerHeader = styled.div`
  width: 100%;
  display: flex;
  align-items: center;
  flex-direction: column;
  gap: ${spacing.plusHalf};

  && > * {
    width: 100%;
  }
`;

const StyledInputSearch = styled(InputSearch)`
  min-width: ${pxToRem(205)};
`;

const Title = styled.div`
  color: ${colors.textPrimary};
  ${heading5}
`;

const FiltersContainer = styled.div`
  display: flex;
  gap: ${spacing.half};
`;

type TemplatePickerHeaderProps = {
  selectedTab: TemplateSource;
  setSelectedTab: Dispatch<SetStateAction<TemplateSource>>;
  selectedTemplate: TemplateMetadata | null;
  search: string;
  setSearch: (value: string) => void;
  showWorkflow: boolean;
  campaignType: CampaignType;
  messageMedium: SelectedMessageMedium;
  templateType: TemplateType[];
  setTemplateType: Dispatch<SetStateAction<TemplateType[]>>;
};

export const TemplatePickerHeader = ({
  selectedTemplate,
  selectedTab,
  setSelectedTab,
  search,
  setSearch,
  showWorkflow,
  campaignType,
  messageMedium,
  templateType,
  setTemplateType,
}: TemplatePickerHeaderProps) => {
  const isApiTriggered = getIsTriggeredCampaignType(campaignType);
  const isWhatsAppMessageMedium = getIsWhatsAppCampaign(
    messageMedium as unknown as AllMessageMedium,
  );
  const { isWhatsAppEnabled } = useWhatsAppMessageChannelFeature();
  const shouldShowWhatsAppFeature = isWhatsAppEnabled && isWhatsAppMessageMedium;

  return (
    <ContainerHeader>
      <ActionsContainer data-test={ATTACH_TEMPLATE_TAKEOVER_HEADER_ACTIONS_ID}>
        {shouldShowWhatsAppFeature ? (
          <Title>{displayContent.templateLabel}</Title>
        ) : (
          <TabGroup selectedTab={selectedTab} onTabChange={(id) => setSelectedTab(id)}>
            <Tab id={BASE}>{displayContent.templateLabel}</Tab>
            <Tab id={CAMPAIGN}>{displayContent.campaignLabel}</Tab>
            {showWorkflow && <Tab id={WORKFLOW}>{displayContent.workflowLabel}</Tab>}
          </TabGroup>
        )}
        <FiltersContainer>
          {shouldShowWhatsAppFeature && isApiTriggered && (
            <>
              {templateType.length > 0 && (
                <GhostButton onClick={() => setTemplateType([])}>Clear</GhostButton>
              )}
              <Menu
                isSelectMenu
                options={TEMPLATE_TYPE_MENU_OPTIONS}
                horizontalAlignment={ALIGNMENT.LEFT}
                defaultSelectedOptionIds={templateType}
                handleSelectionChange={(item: string) => {
                  const _item = item as TemplateType;
                  setTemplateType((prev) =>
                    prev.includes(_item) ? prev.filter((i) => i !== _item) : [...prev, _item],
                  );
                }}
              >
                <FilterMenuTriggerButton selectedFilterCount={templateType.length} />
              </Menu>
            </>
          )}
          <StyledInputSearch
            value={search}
            onChange={setSearch}
            placeholder={displayContent.inputPlaceholder}
            data-test={ATTACH_TEMPLATE_TAKEOVER_SEARCH_INPUT_ID}
            id={ATTACH_TEMPLATE_TAKEOVER_SEARCH_INPUT_ID}
          />
        </FiltersContainer>
      </ActionsContainer>
      {shouldShowWhatsAppFeature && (
        <WhatsAppTemplatePickerBanner
          isApiTriggered={isApiTriggered}
          selectedTemplate={selectedTemplate}
        />
      )}
    </ContainerHeader>
  );
};
